import { PROGRAM_TYPES } from 'utils/programHelpers'
import { toUrlSafe } from 'utils/stringHelpers'

export const SECTION_PHASES = {
  BEFORE: 'before',
  DURING: 'during',
  AFTER: 'after',
}

const ADVISORY_ICON_COLORS_BY_PROGRAM = {
  [PROGRAM_TYPES.ELEMENTARY]: '#03615D',
  [PROGRAM_TYPES.ELEMENTARY_TIER_2]: '#03615D',
  [PROGRAM_TYPES.MIDDLE_SCHOOL]: '#3C6E90',
  [PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2]: '#3C6E90',
}

const DEFAULT_ADVISORY_ICON_COLOR = '#3C6E90'

const getContentTypeId = metadataValue => {
  return typeof metadataValue?.contentType === 'string'
    ? metadataValue.contentType
    : metadataValue?.contentType?.id
}

/**
 * Extracts grade values as-is from metadata values
 * @param {Array} metadataValues - Array of metadata objects
 * @returns {string} - The grade value as-is, or empty string if not found
 */
export const extractGradeValue = metadataValues => {
  if (!metadataValues || !Array.isArray(metadataValues)) {
    return ''
  }

  const gradeMetadata = metadataValues.find(metadataValue => {
    const contentTypeId = getContentTypeId(metadataValue)

    return (
      contentTypeId === 'MetadataGrade' || contentTypeId === 'metadataGrade'
    )
  })

  return gradeMetadata?.grade || ''
}

/**
 * Extracts unit metadata value from metadata values
 * @param {Array} metadataValues - Array of metadata objects
 * @returns {string} - The unit value, or empty string if not found
 */
export const extractUnitValue = metadataValues => {
  if (!metadataValues || !Array.isArray(metadataValues)) {
    return ''
  }

  const unitMetadata = metadataValues.find(metadataValue => {
    const contentTypeId = getContentTypeId(metadataValue)
    return contentTypeId === 'MetadataUnit' || contentTypeId === 'metadataUnit'
  })

  return unitMetadata?.unit || ''
}

/**
 * Gets the breadcrumb navigation path based on the current program type
 * @param {string} currentProgram - The current program type from PROGRAM_TYPES
 * @returns {string} - The breadcrumb navigation path
 */
export const getBreadcrumbPath = currentProgram => {
  if (
    currentProgram === PROGRAM_TYPES.ELEMENTARY ||
    currentProgram === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    return '/product/elementary/lessons'
  } else if (
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    return '/product/middle-school/lessons'
  } else if (currentProgram === PROGRAM_TYPES.HIGH_SCHOOL) {
    return '/product/high-school/learning-experiences'
  }

  // Fallback to dashboard
  return '/dashboard'
}

/**
 * Gets the breadcrumb text based on the current program type
 * @param {string} currentProgram - The current program type from PROGRAM_TYPES
 * @param {string} productTabTitle - Fallback title if program type is unknown
 * @returns {string} - The breadcrumb text
 */
export const getBreadcrumbText = (
  currentProgram,
  productTabTitle = 'Dashboard',
) => {
  if (
    currentProgram === PROGRAM_TYPES.ELEMENTARY ||
    currentProgram === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    return 'Elementary Lessons'
  } else if (
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    currentProgram === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    return 'Middle School Lessons'
  } else if (currentProgram === PROGRAM_TYPES.HIGH_SCHOOL) {
    return 'High School Lessons'
  }

  // Fallback to productTabTitle or Dashboard
  return productTabTitle || 'Dashboard'
}

export const groupAdvisoryActivitiesByType = (resources = []) => {
  const grouped = {}

  resources.forEach(resource => {
    const advisoryType = resource?.advisoryActivityType
    if (!advisoryType) return
    if (!grouped[advisoryType]) {
      grouped[advisoryType] = []
    }
    grouped[advisoryType].push(resource)
  })

  return grouped
}

const titleize = value => {
  return String(value || '')
    .split(/[-_]/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getAdvisoryTypeFromUrl = url => {
  if (!url) return ''

  const pathname = url.startsWith('http')
    ? new URL(url).pathname
    : new URL(url, 'https://learn.secondstep.org').pathname
  const pathParts = pathname.split('/').filter(Boolean)
  const advisoryActivitiesIndex = pathParts.indexOf('advisory-activities')
  const typeSegment = pathParts[advisoryActivitiesIndex + 1] || ''

  if (!typeSegment) return ''

  const singularTypeSegment = typeSegment.endsWith('s')
    ? typeSegment.slice(0, -1)
    : typeSegment

  return titleize(singularTypeSegment)
}

const getRouteFromUrl = url => {
  if (!url) return ''
  const pathname = url.startsWith('http')
    ? new URL(url).pathname
    : new URL(url, 'https://learn.secondstep.org').pathname
  const pathParts = pathname.split('/').filter(Boolean)
  return pathParts[pathParts.length - 1] || ''
}

const mapAdvisoryAssetToResource = asset => {
  const advisoryUrl = asset?.url
  if (!advisoryUrl || !advisoryUrl.includes('/advisory-activities/')) {
    return null
  }

  const advisoryActivityType =
    titleize(asset?.icon) || getAdvisoryTypeFromUrl(advisoryUrl)
  const route = getRouteFromUrl(advisoryUrl)

  if (!advisoryActivityType || !route) {
    return null
  }

  return {
    id: asset?.id || `${advisoryActivityType}-${route}`,
    displayTitle: asset?.display_title || asset?.displayTitle || route,
    advisoryIcon: asset?.icon || '',
    advisoryActivityType,
    pageData: {
      route,
    },
  }
}

export const extractAdvisoryResourcesFromActivityDetails = ({
  activityDetailsList = [],
}) => {
  const advisoryResources = []
  const seenResourceKeys = new Set()

  activityDetailsList.forEach(activityDetail => {
    const contentBlocks = Array.isArray(activityDetail?.content)
      ? activityDetail.content
      : []

    contentBlocks.forEach(contentBlock => {
      const advisoryResource = mapAdvisoryAssetToResource(contentBlock?.asset)
      if (!advisoryResource) {
        return
      }

      const dedupeKey = `${advisoryResource.advisoryActivityType}:${advisoryResource.pageData.route}`
      if (seenResourceKeys.has(dedupeKey)) {
        return
      }

      seenResourceKeys.add(dedupeKey)
      advisoryResources.push(advisoryResource)
    })
  })

  return advisoryResources
}

export const isAdvisoryActivitiesHeaderBlock = contentBlock => {
  return String(contentBlock?.h3 || '')
    .trim()
    .toLowerCase()
    .includes('advisory activities')
}

export const getAdvisoryActivityHref = ({ productName, activity }) => {
  const advisoryActivityType = activity?.advisoryActivityType
  const route = activity?.pageData?.route

  if (!productName || !advisoryActivityType || !route) {
    return ''
  }

  return `/product/${productName}/advisory-activities/${toUrlSafe(
    advisoryActivityType,
  )}s/${toUrlSafe(route)}`
}

export const getAdvisoryIconColorByProgram = currentProgram => {
  return (
    ADVISORY_ICON_COLORS_BY_PROGRAM[currentProgram] ||
    DEFAULT_ADVISORY_ICON_COLOR
  )
}

const getSectionPhase = (section = {}) => {
  const normalizedSectionLabel = String(
    section?.sectionType ||
      section?.phase ||
      section?.type ||
      section?.h2 ||
      section?.h3 ||
      section?.displayTitle ||
      section?.title ||
      '',
  ).toLowerCase()

  if (
    normalizedSectionLabel.includes(SECTION_PHASES.BEFORE) ||
    normalizedSectionLabel.includes('prepare') ||
    normalizedSectionLabel.includes('prep')
  ) {
    return SECTION_PHASES.BEFORE
  }

  if (
    normalizedSectionLabel.includes(SECTION_PHASES.DURING) ||
    normalizedSectionLabel.includes('teach')
  ) {
    return SECTION_PHASES.DURING
  }

  if (
    normalizedSectionLabel.includes(SECTION_PHASES.AFTER) ||
    normalizedSectionLabel.includes('reinforce') ||
    normalizedSectionLabel.includes('reflect')
  ) {
    return SECTION_PHASES.AFTER
  }

  return null
}

const getSectionDisplayLabel = section => {
  const labelCandidate =
    section?.displayTitle || section?.title || section?.h2 || section?.h3

  if (typeof labelCandidate !== 'string') {
    return null
  }

  const trimmedLabel = labelCandidate.trim()
  return trimmedLabel.length ? trimmedLabel : null
}

const getContentBlockWithMarkerHeadingRemoved = ({
  contentBlock,
  markerPhase,
}) => {
  if (!contentBlock || markerPhase == null) {
    return null
  }

  const markerFieldCandidates = [
    'sectionType',
    'phase',
    'type',
    'h2',
    'h3',
    'displayTitle',
    'title',
  ]
  const markerFields = markerFieldCandidates.filter(fieldName => {
    if (!contentBlock[fieldName]) {
      return false
    }

    return (
      getSectionPhase({
        [fieldName]: contentBlock[fieldName],
      }) === markerPhase
    )
  })

  if (!markerFields.length) {
    return contentBlock
  }

  const normalizedContentBlock = {
    ...contentBlock,
  }
  markerFields.forEach(fieldName => {
    normalizedContentBlock[fieldName] = ''
  })

  const hasRenderableValue = Object.entries(normalizedContentBlock).some(
    ([key, value]) => {
      if (
        key === 'contentType' ||
        key === '__typename' ||
        key === 'sectionType' ||
        key === 'phase' ||
        key === 'type'
      ) {
        return false
      }

      return String(value || '').trim().length > 0
    },
  )

  return hasRenderableValue ? normalizedContentBlock : null
}

const splitPhaseSectionsFromContent = (contentBlocks = []) => {
  const normalizedContentBlocks = contentBlocks.filter(Boolean)
  const phaseContentBlocks = {
    [SECTION_PHASES.BEFORE]: [],
    [SECTION_PHASES.DURING]: [],
    [SECTION_PHASES.AFTER]: [],
  }
  const phaseDisplayTitles = {
    [SECTION_PHASES.BEFORE]: null,
    [SECTION_PHASES.DURING]: null,
    [SECTION_PHASES.AFTER]: null,
  }
  const hasAnyPhaseMarker = normalizedContentBlocks.some(
    contentBlock => !!getSectionPhase(contentBlock),
  )
  if (!hasAnyPhaseMarker) {
    return {
      [SECTION_PHASES.BEFORE]: normalizedContentBlocks.length
        ? {
            displayTitle: null,
            content: normalizedContentBlocks,
          }
        : null,
      [SECTION_PHASES.DURING]: null,
      [SECTION_PHASES.AFTER]: null,
    }
  }
  // Preserve leading content when phased markers exist, but keep
  // fallback rendering when no markers are present at all.
  let activePhase = SECTION_PHASES.BEFORE

  normalizedContentBlocks.forEach(contentBlock => {
    const markerPhase = getSectionPhase(contentBlock)
    if (markerPhase) {
      activePhase = markerPhase
      if (!phaseDisplayTitles[markerPhase]) {
        phaseDisplayTitles[markerPhase] = getSectionDisplayLabel(contentBlock)
      }
      const phaseMarkerContentBlock = getContentBlockWithMarkerHeadingRemoved({
        contentBlock,
        markerPhase,
      })
      if (phaseMarkerContentBlock) {
        phaseContentBlocks[activePhase].push(phaseMarkerContentBlock)
      }
      return
    }

    if (activePhase) {
      phaseContentBlocks[activePhase].push(contentBlock)
    }
  })

  return {
    [SECTION_PHASES.BEFORE]: phaseContentBlocks[SECTION_PHASES.BEFORE].length
      ? {
          displayTitle: phaseDisplayTitles[SECTION_PHASES.BEFORE] || 'Before',
          content: phaseContentBlocks[SECTION_PHASES.BEFORE],
        }
      : null,
    [SECTION_PHASES.DURING]: phaseContentBlocks[SECTION_PHASES.DURING].length
      ? {
          displayTitle: phaseDisplayTitles[SECTION_PHASES.DURING] || 'During',
          content: phaseContentBlocks[SECTION_PHASES.DURING],
        }
      : null,
    [SECTION_PHASES.AFTER]: phaseContentBlocks[SECTION_PHASES.AFTER].length
      ? {
          displayTitle: phaseDisplayTitles[SECTION_PHASES.AFTER] || 'After',
          content: phaseContentBlocks[SECTION_PHASES.AFTER],
        }
      : null,
  }
}

export const normalizePhaseSection = ({ section, fallbackTitle }) => {
  if (!section) return null

  const normalizedContent = Array.isArray(section.content)
    ? section.content.map(contentBlock => ({ ...contentBlock }))
    : []

  const headerBlockIndex = normalizedContent.findIndex(contentBlock => {
    return (
      contentBlock?.contentType?.id === 'headerBodyCopy' &&
      typeof contentBlock?.h2 === 'string' &&
      contentBlock.h2.trim().length > 0
    )
  })

  if (headerBlockIndex === -1) {
    return {
      ...section,
      displayTitle: section?.displayTitle || fallbackTitle,
      content: normalizedContent,
    }
  }

  const sectionTitle = normalizedContent[headerBlockIndex].h2.trim()
  normalizedContent[headerBlockIndex] = {
    ...normalizedContent[headerBlockIndex],
    h2: '',
  }

  return {
    ...section,
    displayTitle: sectionTitle || section?.displayTitle || fallbackTitle,
    content: normalizedContent,
  }
}

export const getPhaseSections = ({
  activityDetailsList,
  activeActivityDetail,
  entry,
}) => {
  const phaseSections = {
    [SECTION_PHASES.BEFORE]: null,
    [SECTION_PHASES.DURING]: null,
    [SECTION_PHASES.AFTER]: null,
  }

  activityDetailsList.forEach(section => {
    const sectionPhase = getSectionPhase(section)
    if (sectionPhase && !phaseSections[sectionPhase]) {
      phaseSections[sectionPhase] = section
    }
  })

  if (!phaseSections[SECTION_PHASES.BEFORE] && entry?.beforeSection) {
    phaseSections[SECTION_PHASES.BEFORE] = entry.beforeSection
  }
  if (!phaseSections[SECTION_PHASES.DURING] && entry?.duringSection) {
    phaseSections[SECTION_PHASES.DURING] = entry.duringSection
  }
  if (!phaseSections[SECTION_PHASES.AFTER] && entry?.afterSection) {
    phaseSections[SECTION_PHASES.AFTER] = entry.afterSection
  }

  if (
    !phaseSections[SECTION_PHASES.BEFORE] &&
    !phaseSections[SECTION_PHASES.DURING] &&
    !phaseSections[SECTION_PHASES.AFTER] &&
    Array.isArray(activeActivityDetail?.content)
  ) {
    return splitPhaseSectionsFromContent(activeActivityDetail.content)
  }

  return phaseSections
}
